/** Fleet Control workspace layout presets and persisted sizing. */

export type FleetWorkspacePreset = "unified" | "operator";

export type FleetWorkspacePanelId =
  | "transcript"
  | "directory"
  | "controls"
  | "analytics"
  | "inspector";

export type FleetWorkspaceVisiblePanels = Record<FleetWorkspacePanelId, boolean>;

export interface FleetWorkspaceLayoutState {
  /** Column fr weights: left, center, right (operator) or left, right (2-col). */
  columns: [number, number, number];
  /** Row fr weights for 2-row layouts (currently unused: only `unified` is shipped). */
  rows: [number, number];
  visiblePanels: FleetWorkspaceVisiblePanels;
}

export type FleetWorkspaceDensity = "compact" | "comfortable";

export type FleetInspectorTabId =
  | "agents"
  | "execution"
  | "activity"
  | "health"
  | "files"
  | "subagents"
  | "document"
  | "tunnels";

export const FLEET_INSPECTOR_TABS: Array<{ id: FleetInspectorTabId; label: string }> = [
  { id: "agents", label: "Agents" },
  { id: "execution", label: "Execution" },
  { id: "activity", label: "Activity" },
  { id: "subagents", label: "Subagents" },
  { id: "document", label: "Document" },
  { id: "health", label: "Health" },
  { id: "files", label: "Files" },
  { id: "tunnels", label: "Tunnels" },
];

const UNIFIED_DEFAULT: FleetWorkspaceLayoutState = {
  columns: [0.58, 0, 0.42],
  rows: [1, 0],
  visiblePanels: {
    transcript: true,
    directory: false,
    controls: false,
    analytics: false,
    inspector: true,
  },
};

const OPERATOR_DEFAULT: FleetWorkspaceLayoutState = {
  columns: [0, 1.35, 0.65],
  rows: [1, 0],
  visiblePanels: {
    transcript: true,
    directory: false,
    controls: false,
    analytics: false,
    inspector: true,
  },
};

export const DEFAULT_FLEET_WORKSPACE_LAYOUTS: Record<
  FleetWorkspacePreset,
  FleetWorkspaceLayoutState
> = {
  unified: cloneLayout(UNIFIED_DEFAULT),
  operator: cloneLayout(OPERATOR_DEFAULT),
};

export function defaultVisiblePanels(): FleetWorkspaceVisiblePanels {
  return {
    transcript: true,
    directory: true,
    controls: true,
    analytics: true,
    inspector: true,
  };
}

export function normalizeFleetWorkspacePreset(raw: unknown): FleetWorkspacePreset {
  if (raw === "unified" || raw === "operator") {
    return raw;
  }
  return "unified";
}

export function normalizeFleetWorkspaceDensity(raw: unknown): FleetWorkspaceDensity {
  return raw === "compact" ? "compact" : "comfortable";
}

function normalizeRatioTriple(
  raw: unknown,
  fallback: [number, number, number],
): [number, number, number] {
  if (!Array.isArray(raw) || raw.length !== 3) {
    return fallback;
  }
  const nums = raw.map((v) => (typeof v === "number" && Number.isFinite(v) && v > 0.05 ? v : null));
  if (nums.some((v) => v === null)) {
    return fallback;
  }
  return nums as [number, number, number];
}

function normalizeRatioPair(raw: unknown, fallback: [number, number]): [number, number] {
  if (!Array.isArray(raw) || raw.length !== 2) {
    return fallback;
  }
  const a = typeof raw[0] === "number" && raw[0] > 0.05 ? raw[0] : null;
  const b = typeof raw[1] === "number" && raw[1] > 0.05 ? raw[1] : null;
  if (a === null || b === null) {
    return fallback;
  }
  return [a, b];
}

function normalizeVisiblePanels(
  raw: unknown,
  fallback: FleetWorkspaceVisiblePanels,
): FleetWorkspaceVisiblePanels {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...fallback };
  }
  const src = raw as Partial<FleetWorkspaceVisiblePanels>;
  return {
    transcript: typeof src.transcript === "boolean" ? src.transcript : fallback.transcript,
    directory: typeof src.directory === "boolean" ? src.directory : fallback.directory,
    controls: typeof src.controls === "boolean" ? src.controls : fallback.controls,
    analytics: typeof src.analytics === "boolean" ? src.analytics : fallback.analytics,
    // JAISIU-3633 (operator bug 2026-09-28): the topbar Inspector
    // toggle used to drive `visiblePanels.inspector = false` to hide
    // the panel; the JAISIU-3603 followup chain moved that driver to
    // `state.settings.fleetUnifiedDrawerOpen` instead. Any browser
    // that still has `inspector: false` saved in localStorage from the
    // old behaviour would now have the panel permanently pinned
    // hidden, because the unified-layout visibility gate still ANDs
    // `visiblePanels.inspector !== false`. Force `inspector: true`
    // here so saved state from before the fix is migrated on hydrate
    // — the visibility flag is now exclusively driven by
    // `fleetUnifiedDrawerOpen`.
    inspector: true,
  };
}

export function normalizeFleetWorkspaceLayoutState(
  raw: unknown,
  preset: FleetWorkspacePreset,
): FleetWorkspaceLayoutState {
  const fallback = DEFAULT_FLEET_WORKSPACE_LAYOUTS[preset];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return cloneLayout(fallback);
  }
  const src = raw as Partial<FleetWorkspaceLayoutState>;
  return {
    columns: normalizeRatioTriple(src.columns, fallback.columns),
    rows: normalizeRatioPair(src.rows, fallback.rows),
    visiblePanels: normalizeVisiblePanels(src.visiblePanels, fallback.visiblePanels),
  };
}

export function migrateFleetWorkspaceLayouts(
  raw: unknown,
): Record<FleetWorkspacePreset, FleetWorkspaceLayoutState> {
  const src =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Partial<Record<FleetWorkspacePreset, unknown>>)
      : {};
  return {
    unified: normalizeFleetWorkspaceLayoutState(src.unified, "unified"),
    operator: normalizeFleetWorkspaceLayoutState(src.operator, "operator"),
  };
}

export function resetFleetWorkspaceLayout(preset: FleetWorkspacePreset): FleetWorkspaceLayoutState {
  return cloneLayout(DEFAULT_FLEET_WORKSPACE_LAYOUTS[preset]);
}

/** Map legacy fleetChatMainTab to inspector tab. */
export function fleetMainTabToInspectorTab(
  tab: import("./fleet-chat-main-tabs.ts").FleetChatMainTabId,
): FleetInspectorTabId {
  switch (tab) {
    case "subagents":
      return "subagents";
    case "projects":
      return "files";
    case "files":
      return "files";
    case "fleetMap":
      return "agents";
    default:
      return "agents";
  }
}

function cloneLayout(layout: FleetWorkspaceLayoutState): FleetWorkspaceLayoutState {
  return {
    columns: [...layout.columns],
    rows: [...layout.rows],
    visiblePanels: { ...layout.visiblePanels },
  };
}
